import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDocumentStore } from '../storage/index.ts';
import { DocumentEditor } from './DocumentEditor.tsx';
import { ErrorBoundary, ErrorFallback } from '../errors/index.ts';

function Message({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3 p-8">
      {children}
      <Link to="/" className="text-sm text-blue-700 hover:underline">
        Back to documents
      </Link>
    </div>
  );
}

export function EditorPage() {
  const { id } = useParams<{ id: string }>();
  const store = useDocumentStore();
  const queryClient = useQueryClient();

  const loaded = useQuery({
    queryKey: ['document', id],
    queryFn: () => (id ? store.get(id) : null),
    enabled: Boolean(id),
    // The editor owns the document once it is open; refetching underneath it
    // would fight the user's caret.
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  if (loaded.isPending) {
    return <p className="p-8 text-sm text-neutral-500">Opening document…</p>;
  }

  if (loaded.isError) {
    // The real cause is on the console; the user gets something actionable.
    console.error('[burfy-docs] failed to open document', loaded.error);
    return (
      <Message>
        <p className="text-sm text-red-700" role="alert">
          This document could not be opened. It may have been saved by a newer version of
          Burfy Docs.
        </p>
      </Message>
    );
  }

  if (!loaded.data) {
    return (
      <Message>
        <p className="text-sm text-neutral-700">This document no longer exists.</p>
      </Message>
    );
  }

  // Keyed by id so opening a different document remounts with a fresh draft
  // rather than trying to reconcile one document's state onto another's.
  //
  // The boundary is inside the route rather than only around the app: a
  // document whose content makes the editor throw would otherwise be
  // unopenable for ever, with no way back to the others. `resetKey` clears the
  // failure when a different document is opened, so one bad document does not
  // make the next one look broken too.
  return (
    <ErrorBoundary
      resetKey={loaded.data.id}
      fallback={(error, reset) => (
        <ErrorFallback
          error={error}
          onRetry={() => {
            // Refetch before remounting. This query is `staleTime: Infinity`
            // and nothing invalidates it, so retrying on the cached snapshot
            // would restore the document as it was when *opened* — losing
            // every edit since from the screen, and pinning a stale `rev` that
            // makes each later autosave fail with a conflict. The editor would
            // look recovered and be unable to save until a full reload.
            void queryClient.invalidateQueries({ queryKey: ['document', id] });
            reset();
          }}
        />
      )}
    >
      <DocumentEditor key={loaded.data.id} document={loaded.data} store={store} />
    </ErrorBoundary>
  );
}
